import React, { useEffect, useMemo, useState } from 'react';
import { useIsEditionAwarenessPillDismissed } from '@atlassian/jira-edition-awareness-for-standard-to-premium/src/services/is-ea-pill-dismissed/index.tsx';
import { useIsEligibleForUpsellEa } from '@atlassian/jira-edition-awareness-for-standard-to-premium/src/services/is-eligible-for-upsell-ea/index.tsx';
import { JSErrorBoundary } from '@atlassian/jira-error-boundaries/src/ui/js-error-boundary/JSErrorBoundary.tsx';
import {
	ContextualAnalyticsData,
	useAnalyticsEvents,
} from '@atlassian/jira-product-analytics-bridge';
import { useIsSiteAdmin } from '@atlassian/jira-tenant-context-controller/src/components/is-site-admin/index.tsx';
import UFOSegment from '@atlassian/jira-ufo-segment/src/index.tsx';
import {
	EditionAwareness as EditionAwarenessImpl,
	type EditionAwarenessProps,
} from '@atlassian/edition-awareness';
import { EditionAwarenessUpsell } from '@atlassian/edition-awareness/ui/edition-awareness-upsell';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { memoizedFetchSitePersonalization } from '@atlassian/jira-personalization-service/src/services/site/index.tsx';
import type { Trait } from '@atlassian/jira-personalization-service/src/types.tsx';
import fireErrorAnalytics from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';

import { useAccountId } from '@atlassian/jira-tenant-context-controller/src/components/account-id/index.tsx';
import { useEnvironment } from '@atlassian/jira-tenant-context-controller/src/components/environment/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { useProductAndEdition } from '../services/use-product-and-edition.tsx';
import { useReverseTrials } from '../services/use-reverse-trials.tsx';

const EditionAwarenessPlatformInnerWithRTCheck = (props: EditionAwarenessProps) => {
	const { isReady, isInReverseTrial, onOpenOnboardingMenu } = useReverseTrials();

	// We need to wait for the reverse trial check to be complete before rendering the button
	const canRender = props.canRender && isReady;

	return (
		<EditionAwarenessImpl
			{...props}
			isInReverseTrial={isInReverseTrial}
			onOpenOnboardingMenu={onOpenOnboardingMenu}
			canRender={canRender}
		/>
	);
};

const EditionAwarenessPlatformInner = (props: EditionAwarenessProps) => {
	const { isEligibleForUpsellEa } = useIsEligibleForUpsellEa({
		product: props.product,
		edition: props.edition,
		isSiteAdmin: Boolean(props.userRoles?.includes('SITE_ADMIN')),
	});

	return isEligibleForUpsellEa ? (
		<EditionAwarenessUpsell
			{...(({ product, cloudId, edition, userRoles, hasDismissedButton, onDismissButton }) => ({
				product,
				cloudId,
				edition,
				userRoles,
				hasDismissedButton,
				onDismissButton,
			}))(props)}
		/>
	) : (
		<EditionAwarenessImpl {...props} />
	);
};

const EditionAwarenessPlatform = () => {
	const cloudId = useCloudId();
	const accountId = useAccountId();
	const environment = useEnvironment();
	const isSiteAdmin = useIsSiteAdmin();
	const { product, edition } = useProductAndEdition();
	const { isDismissed, handleDismiss } = useIsEditionAwarenessPillDismissed();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	// Calculate which editions need to fetch traits

	const [siteTraits, setSiteTraits] = useState<Trait[] | undefined>(() => {
		if (edition === 'standard' && product === 'jira' && fg('ea_traits_for_jira_standard')) {
			return undefined;
		}
		return edition === 'free' ? undefined : [];
	});

	useEffect(() => {
		if (siteTraits === undefined) {
			memoizedFetchSitePersonalization({ cloudId })
				.then((traits: Trait[]) => {
					setSiteTraits(traits);
				})
				.catch((e) => {
					setSiteTraits([]);
					const event = createAnalyticsEvent({});
					fireErrorAnalytics({
						event,
						meta: {
							id: 'editionAwarenessGrowthCohortTrait',
							packageName: 'edition-awareness',
							teamName: 'growth-tako',
						},
						error: e,
						sendToPrivacyUnsafeSplunk: true,
					});
				});
		}
	}, [cloudId, createAnalyticsEvent, edition, siteTraits]);

	if (!product || !edition) {
		return null;
	}

	if (!accountId && fg('edition_awareness_account_id_required')) {
		// We don't render when the customer is not logged in
		return null;
	}

	// We need to wait for the siteType to be set before rendering the button
	const canRender = siteTraits !== undefined;

	const editionAwarenessProps: EditionAwarenessProps = fg('jfp-magma-stability-check-nav4-batch-2')
		? // eslint-disable-next-line react-hooks/rules-of-hooks
			useMemo(
				() => ({
					edition,
					cloudId,
					product,
					accountId,
					environment,
					userRoles: isSiteAdmin ? ['SITE_ADMIN'] : [],
					hasDismissedButton: isDismissed,
					onDismissButton: handleDismiss,
					siteTraits,
					canRender,
				}),
				[
					accountId,
					canRender,
					cloudId,
					edition,
					environment,
					handleDismiss,
					isDismissed,
					isSiteAdmin,
					product,
					siteTraits,
				],
			)
		: {
				edition,
				cloudId,
				product,
				accountId,
				environment,
				userRoles: isSiteAdmin ? ['SITE_ADMIN'] : [],
				hasDismissedButton: isDismissed,
				onDismissButton: handleDismiss,
				siteTraits,
				canRender,
			};

	return (
		<ContextualAnalyticsData
			attributes={{
				isSiteAdmin,
				product,
				edition,
			}}
		>
			{edition === 'premium' && product === 'jira' ? (
				// We need to call a few hooks to check RT, could be cleaned up in the future
				<EditionAwarenessPlatformInnerWithRTCheck {...editionAwarenessProps} />
			) : (
				<EditionAwarenessPlatformInner {...editionAwarenessProps} />
			)}
		</ContextualAnalyticsData>
	);
};

export const EditionAwareness = () => {
	// This component is not rendered on the server, we don't have enough information to determine if it will be renderer or not
	// Don't remove because it may cause the SSR to fail, at this point it because the EditonAwarenessButton is using useLazyLoadQuery
	if (__SERVER__) {
		return null;
	}

	return (
		<JSErrorBoundary
			id="EditionAwareness"
			packageName="jiraNavigationAppsSidebarEditionAwareness"
			fallback="unmount"
			teamName="growth-tako"
		>
			<UFOSegment name="edition-awareness">
				<EditionAwarenessPlatform />
			</UFOSegment>
		</JSErrorBoundary>
	);
};
